import NiceModal, { useModal } from "@ebay/nice-modal-react"
import { Check, Clipboard, KeyRound, Link2, Loader2 } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { useComputers } from "../../computers/context"
import { readPairingInvite } from "../../computers/pairing"
import { updatePersonProfile } from "../../computers/peopleAdmin"
import { type LinkCode, parseLinkCode, suggestLinkWords } from "../../link/codes"
import { type LinkCard, type LinkIdentity, type LinkOffer, joinLink, offerLink } from "../../link/linkFlow"
import { createRendezvousTransport } from "../../link/rendezvous"
import { useOptionalCodeStore } from "../../store/context"
import { copyTextToClipboard } from "../../utils/clipboard"
import { PersonGlyph } from "../computers/PersonGlyph"
import { Modal } from "../ui/Modal"
import { Segmented } from "./Segmented"
import { expiresInWords } from "./sharingCopy"

interface LinkSheetProps {
    nodeId: string
}

type Mode = "show" | "enter"
type Stage = "idle" | "waiting" | "linking" | "done" | "error"

const MODES = [
    { id: "show", label: "Show a code" },
    { id: "enter", label: "Enter a code" },
] as const

function relayUrlFor(routes: ReturnType<typeof useComputers>["state"]["computers"][number]["routes"]): string | null {
    const relay = routes.find((route) => route.type === "relay")
    return relay && relay.type === "relay" ? relay.relayUrl : null
}

/**
 * Two people, one short code. Showing a code puts it in the center of the
 * sheet like a verification number; entering one is a single field with
 * word suggestions. Both end on the same screen: two glyphs, linked.
 */
export const LinkSheet = NiceModal.create(({ nodeId }: LinkSheetProps) => {
    const modal = useModal()
    const computers = useComputers()
    const codeStore = useOptionalCodeStore()
    const computer = computers.state.computers.find((candidate) => candidate.nodeId === nodeId)
    const serviceUrl = computer ? relayUrlFor(computer.routes) : null
    const profileSettings = codeStore?.personalSettingsStore?.settings
    const profile = profileSettings?.current.profile
    const needsOwnName = !profile?.name
    const [ownName, setOwnName] = useState(() => profile?.name ?? "")
    const [mode, setMode] = useState<Mode>("show")
    const [stage, setStage] = useState<Stage>("idle")
    const [offer, setOffer] = useState<LinkOffer | null>(null)
    const [codeInput, setCodeInput] = useState("")
    const [linked, setLinked] = useState<LinkCard | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [copied, setCopied] = useState(false)
    const [now, setNow] = useState(() => Date.now())
    const abort = useRef<AbortController | null>(null)
    const offerRef = useRef<LinkOffer | null>(null)

    const identity: LinkIdentity = {
        name: ownName.trim() || profile?.name?.trim() || computer?.name || "WildLark user",
        ...(profile?.glyph ? { glyph: profile.glyph } : {}),
        ...(profile?.color ? { color: profile.color } : {}),
    }

    useEffect(() => {
        const interval = globalThis.setInterval(() => setNow(Date.now()), 15_000)
        return () => {
            globalThis.clearInterval(interval)
            abort.current?.abort()
            void offerRef.current?.cancel()
        }
    }, [])

    const rememberOwnName = () => {
        if (needsOwnName && ownName.trim() && profileSettings) profileSettings.set({ profile: { ...profileSettings.current.profile, name: ownName.trim() } })
    }

    const mintInvite = (personName: string) => computers.startLink(nodeId, personName)

    const finishLink = async (card: LinkCard, renamePrincipalId?: string) => {
        setStage("linking")
        const invite = readPairingInvite(card.invite)
        if (!invite) throw new Error(`${card.name} sent an invite this app cannot read.`)
        await computers.pairAndAdd(invite)
        if (renamePrincipalId) {
            await updatePersonProfile(computers.backend, nodeId, renamePrincipalId, {
                name: card.name,
                ...(card.glyph ? { glyph: card.glyph } : {}),
                ...(card.color ? { color: card.color } : {}),
            })
        }
        setLinked(card)
        setStage("done")
    }

    const startOffer = async () => {
        if (!serviceUrl) return
        if (needsOwnName && !ownName.trim()) {
            setError("Add your name first so they know who they're linking with.")
            return
        }
        rememberOwnName()
        setStage("waiting")
        setError(null)
        const controller = new AbortController()
        abort.current = controller
        try {
            const created = await offerLink({ transport: createRendezvousTransport(serviceUrl), identity, mintInvite, signal: controller.signal })
            offerRef.current = created
            setOffer(created)
            const card = await created.completion
            await finishLink(card)
        } catch (cause) {
            if (controller.signal.aborted) return
            setError(cause instanceof Error ? cause.message : String(cause))
            setStage("error")
        }
    }

    const join = async () => {
        if (!serviceUrl) return
        const code: LinkCode | null = parseLinkCode(codeInput)
        if (!code) {
            setError("That doesn't look like a WildLark code. It reads like 7-lantern-fox.")
            return
        }
        if (needsOwnName && !ownName.trim()) {
            setError("Add your name first so they know who they're linking with.")
            return
        }
        rememberOwnName()
        setStage("waiting")
        setError(null)
        const controller = new AbortController()
        abort.current = controller
        let mintedPrincipalId: string | undefined
        try {
            const card = await joinLink(code, {
                transport: createRendezvousTransport(serviceUrl),
                identity,
                mintInvite: async (personName) => {
                    const minted = await mintInvite(personName)
                    mintedPrincipalId = minted.principalId
                    return minted
                },
                signal: controller.signal,
            })
            await finishLink(card, mintedPrincipalId)
        } catch (cause) {
            if (controller.signal.aborted) return
            setError(cause instanceof Error ? cause.message : String(cause))
            setStage("error")
        }
    }

    const copyCode = async () => {
        if (!offer) return
        try {
            await copyTextToClipboard(`Link with me on WildLark: ${offer.codeText}`)
            setCopied(true)
            globalThis.setTimeout(() => setCopied(false), 1_500)
        } catch (cause) {
            setError(cause instanceof Error ? cause.message : String(cause))
        }
    }

    const switchMode = (next: Mode) => {
        if (stage === "waiting" || stage === "linking") return
        setMode(next)
        setError(null)
    }

    const suggestions = mode === "enter" ? suggestLinkWords(codeInput.split(/[-\s]+/).pop() ?? "") : []
    const busy = stage === "waiting" || stage === "linking"

    const close = () => {
        modal.resolve()
        void modal.hide()
    }

    if (!serviceUrl) {
        return (
            <Modal title="Link with a code" onClose={close} size="md">
                <p className="text-sm leading-6 text-muted">
                    Turn on <span className="font-medium text-base-content">Reachable from anywhere</span> for {computer?.name ?? "this computer"} first.
                    Linking rides on the same private relay your colleague uses to reach you.
                </p>
            </Modal>
        )
    }

    if (stage === "done" && linked) {
        return (
            <Modal title="Linked" onClose={close} size="md">
                <div className="flex flex-col items-center gap-5 py-6 text-center">
                    <div className="flex items-center gap-4">
                        <PersonGlyph name={identity.name} glyph={identity.glyph} color={identity.color} size="xl" />
                        <Link2 className="h-5 w-5 text-primary" />
                        <PersonGlyph name={linked.name} glyph={linked.glyph} color={linked.color} size="xl" />
                    </div>
                    <div>
                        <div className="text-xl font-semibold tracking-tight text-base-content">You and {linked.name} are linked</div>
                        <p className="mx-auto mt-2 max-w-sm text-sm leading-6 text-muted">
                            Nothing is shared yet. Open the menu beside {linked.name}'s name and choose Change access to open a project; they can do the same
                            for you.
                        </p>
                    </div>
                    <button
                        type="button"
                        className="btn flex h-11 items-center justify-center bg-primary px-6 text-sm font-medium text-primary-content"
                        onClick={close}
                    >
                        Done
                    </button>
                </div>
            </Modal>
        )
    }

    return (
        <Modal title="Link with a code" onClose={close} size="md">
            <div className="flex flex-col gap-6">
                <Segmented options={MODES} value={mode} onChange={switchMode} label="Linking method" />

                {needsOwnName && (
                    <label className="flex flex-col gap-2">
                        <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted">Your name, as they'll see it</span>
                        <input
                            className="input h-11 w-full border border-border bg-input px-3 text-sm text-base-content outline-none focus:border-primary"
                            value={ownName}
                            onChange={(event) => setOwnName(event.target.value)}
                            placeholder="Your name"
                            autoComplete="name"
                            maxLength={80}
                            disabled={busy}
                        />
                    </label>
                )}

                {mode === "show" ? (
                    <div className="flex flex-col items-center gap-5 py-2 text-center">
                        {offer ? (
                            <>
                                <div
                                    className="font-mono text-[40px] font-semibold leading-none tracking-[0.16em] text-base-content"
                                    data-link-code={offer.codeText}
                                >
                                    {offer.codeText}
                                </div>
                                <p className="max-w-sm text-sm leading-6 text-muted">
                                    Say it on a call or paste it in a chat. It works once and expires in {expiresInWords(offer.expiresAt, now)}. Keep this open
                                    while they type it in.
                                </p>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        className="btn flex h-10 items-center gap-2 border border-border px-4 text-sm font-medium text-base-content hover:bg-base-200"
                                        onClick={() => void copyCode()}
                                    >
                                        {copied ? <Check className="h-4 w-4 text-success" /> : <Clipboard className="h-4 w-4" />}
                                        {copied ? "Copied" : "Copy message"}
                                    </button>
                                </div>
                                <div className="flex items-center gap-2 text-xs text-muted" aria-live="polite">
                                    {stage === "error" ? null : <span className="sharing-pulse inline-block h-1.5 w-1.5 bg-primary" />}
                                    {stage === "linking" ? "Exchanging introductions…" : stage === "error" ? "" : "Waiting for them to enter it"}
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="flex h-16 w-16 items-center justify-center border border-border bg-base-200">
                                    <KeyRound className="h-6 w-6 text-primary" />
                                </div>
                                <p className="max-w-sm text-sm leading-6 text-muted">
                                    A short code like <span className="font-mono text-base-content">7-lantern-fox</span>. The relay introduces the two apps but
                                    can't read who linked with whom.
                                </p>
                                <button
                                    type="button"
                                    className="btn flex h-11 items-center gap-2 bg-primary px-6 text-sm font-medium text-primary-content disabled:opacity-50"
                                    onClick={() => void startOffer()}
                                    disabled={busy}
                                >
                                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                                    Make a code
                                </button>
                            </>
                        )}
                    </div>
                ) : (
                    <div className="flex flex-col gap-4">
                        <input
                            className="input h-14 w-full border border-border bg-input px-4 text-center font-mono text-2xl tracking-[0.12em] text-base-content outline-none focus:border-primary"
                            value={codeInput}
                            onChange={(event) => setCodeInput(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === "Enter" && !busy) {
                                    event.preventDefault()
                                    void join()
                                }
                            }}
                            placeholder="7-lantern-fox"
                            autoFocus
                            autoCapitalize="none"
                            autoCorrect="off"
                            autoComplete="off"
                            spellCheck={false}
                            disabled={busy}
                            aria-label="Their code"
                        />
                        {suggestions.length > 0 && !busy && (
                            <div className="flex flex-wrap justify-center gap-1">
                                {suggestions.map((word) => (
                                    <button
                                        type="button"
                                        key={word}
                                        className="btn border border-border px-2 py-1 font-mono text-xs text-muted hover:bg-base-200 hover:text-base-content"
                                        onClick={() => setCodeInput((current) => current.replace(/[a-z]*$/, word))}
                                    >
                                        {word}
                                    </button>
                                ))}
                            </div>
                        )}
                        <button
                            type="button"
                            className="btn flex h-11 w-full items-center justify-center gap-2 bg-primary text-sm font-medium text-primary-content disabled:opacity-50"
                            disabled={busy}
                            onClick={() => void join()}
                        >
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
                            {stage === "linking" ? "Exchanging introductions…" : stage === "waiting" ? "Waiting for the other side…" : "Link"}
                        </button>
                    </div>
                )}

                {error && (
                    <div role="alert" className="border border-error/40 bg-error/10 px-3 py-2 text-sm text-error">
                        {error}
                        {mode === "show" && stage === "error" && (
                            <button type="button" className="btn ml-2 inline-flex items-center underline" onClick={() => void startOffer()}>
                                Make a new code
                            </button>
                        )}
                    </div>
                )}
            </div>
        </Modal>
    )
})
